import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

/**
 * CompareContext — a browser-local list of up to 3 product IDs the buyer
 * is weighing against each other. Persisted to localStorage so the list
 * survives navigation and page reloads (buyers routinely toggle Compare
 * between the catalog grid and individual detail pages).
 *
 * Anonymous-friendly: unlike Cart/Favorites this doesn't require login,
 * which matters because "help me decide" is a mid-funnel action and
 * gating it behind auth would kill adoption.
 */
const MAX_ITEMS = 3;
const STORAGE_KEY = "mazad_compare_ids";

const CompareCtx = createContext({
  ids: [],
  count: 0,
  isFull: false,
  has: () => false,
  toggle: () => {},
  remove: () => {},
  clear: () => {},
});

export function CompareProvider({ children }) {
  const [ids, setIds] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, MAX_ITEMS) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch { /* quota — ignore */ }
  }, [ids]);

  const has = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback((id) => {
    if (!id) return;
    setIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= MAX_ITEMS) {
        toast.error(`Compare limit reached — remove one to add another (max ${MAX_ITEMS})`);
        return prev;
      }
      return [...prev, id];
    });
  }, []);

  const remove = useCallback((id) => setIds((prev) => prev.filter((x) => x !== id)), []);
  const clear = useCallback(() => setIds([]), []);

  const value = useMemo(
    () => ({ ids, count: ids.length, isFull: ids.length >= MAX_ITEMS, has, toggle, remove, clear }),
    [ids, has, toggle, remove, clear],
  );

  return <CompareCtx.Provider value={value}>{children}</CompareCtx.Provider>;
}

export const useCompare = () => useContext(CompareCtx);
export const COMPARE_MAX = MAX_ITEMS;
